import { ValueSetComposeIncludeConcept } from 'src/careflow/ph-core/fhir-client/resources';
import { createWrapper } from 'src/careflow/ph-core/fhir-client/fhir-react/base';

/**
 * Utility function to sort ValueSet concepts alphabetically by display name
 * @param list - Array of ValueSetComposeIncludeConcept to sort
 * @returns Sorted array of concepts
 */
const sortValueSetConceptsByDisplay = (list: ValueSetComposeIncludeConcept[]): ValueSetComposeIncludeConcept[] => {
  return list.sort(function (a, b) {
    // Handle undefined displays
    if (a?.display === undefined || b?.display === undefined) {
      return 0;
    }

    const displayA = a?.display ? a?.display.toUpperCase() : a?.display;
    const displayB = b?.display ? b?.display.toUpperCase() : b?.display;

    if (displayA < displayB) {
      return -1;
    }
    if (displayA > displayB) {
      return 1;
    }
    return 0;
  });
};

export const ValueSetWrapper = createWrapper('ValueSet', (valueSet) => ({
  getFirstIncludeConceptSystem: () => valueSet?.compose?.include?.[0]?.system,
  
  asList(): ValueSetComposeIncludeConcept[] {
    return this.asSortedListAll();
  },

  asListAll: (): ValueSetComposeIncludeConcept[] => {
    const list = valueSet?.compose?.include?.reduce?.<ValueSetComposeIncludeConcept[]>((includes, include) => {
      if (!include?.concept?.length) return includes;
      return [
        ...includes,
        ...include.concept.reduce<ValueSetComposeIncludeConcept[]>(
          (concepts, { code, display }) => {
            if (!code) return concepts;
            return [
              ...concepts,
              {
                code,
                display,
                system: include?.system,
              },
            ];
          },
          []
        ),
      ];
    }, []) ?? [];
    
    return sortValueSetConceptsByDisplay(list);
  },

  asSortedListAll(): ValueSetComposeIncludeConcept[] {
    return this.asListAll();
  },

  asListForInput(
    mapper: {
      [key: string]: string;
    } = {
      display: 'label',
      code: 'value',
    }
  ) {
    const list = this.asListAll();
    if (!list || list.length <= 0) return [];
    return list.map((el: ValueSetComposeIncludeConcept) => {
      const mapped: {
        [key: string]: string;
      } = {};
      for (const [key, value] of Object.entries(mapper)) {
        if (typeof key === 'string') {
          mapped[value] = key;
        }
      }
      return mapped;
    });
  },

  filterExpansionSystems(systems: string[]) {
    if (!valueSet?.expansion?.contains) return [];

    const matched = valueSet?.expansion?.contains.filter((x) =>
      x?.system ? systems.indexOf(x?.system) > -1 : false
    );

    return {
      ...valueSet,
      expansion: {
        ...valueSet.expansion,
        contains: matched,
      },
    };
  },

  getConceptSystem: (): string | undefined => valueSet?.compose?.include?.[0]?.system,

  useContext: () => valueSet?.useContext || [],
}));

export type WrappedValueSet = ReturnType<typeof ValueSetWrapper>;
